import { ExperienceTypes, TimeZoneDisplay } from '@/types/gql.generated';
import { getLocalTimeZone, resolveTimeZone } from '@/utils/dates';
import type { PageExperienceFragment } from '../fragments/PageExperience.generated';
import type { PageExperience } from '../types';

export const DEFAULT_TITLE = 'My new page';

export const getEditLink = (experience: Pick<PageExperience, 'id'>): string => {
  return `/experience/${experience.id}/edit`;
};

/**
 * Whether an experience is a planner surface — the planner itself or one of its
 * child views.
 *
 * Legacy standalone Page/Printable experiences share the same card, layout and
 * modal components, so planner-only behavior has to gate on this or it leaks
 * into them. `useIsPlanner` is the same check for components that read the
 * experience from context.
 */
export const isPlannerSurface = (
  experience: Pick<PageExperience, 'type' | 'isPlannerChild'> | undefined
): boolean =>
  experience?.type === ExperienceTypes.Planner ||
  Boolean(experience?.isPlannerChild);

/**
 * Whether an experience draws from the palette accent system rather than the
 * legacy page HSL: the same planner surfaces, under the name the color code
 * reads it as.
 */
export const usesPlannerColorSystem = isPlannerSurface;

export const getAuthorTimezone = (
  experience: PageExperienceFragment
): string => {
  // Sanitize the stored author zone too: bad data would crash luxon the same
  // way an unsupported local zone does (Sentry WEB-CLIENT-265).
  return experience.timeZone
    ? resolveTimeZone(experience.timeZone)
    : getLocalTimeZone();
};

/**
 * Whether the category legend should render. The legend maps category colors to
 * their names, so it says nothing once Category color is off and the events
 * carry no color to look up — the Views panel greys the legend toggle out to
 * match. Takes only the two flags it reads so trimmed selection sets work.
 */
export const isColorLegendVisible = (
  experience: Pick<
    PageExperience,
    'colorLegendEnabled' | 'eventCardCategoryEnabled'
  >
): boolean =>
  experience.colorLegendEnabled && experience.eventCardCategoryEnabled;

export const getDisplayTimezone = (
  experience: PageExperienceFragment
): string => {
  if (experience.timeZoneDisplay === TimeZoneDisplay.Experience) {
    return getAuthorTimezone(experience);
  }

  return getLocalTimeZone();
};
